import { RocketIcon, SparklesIcon } from 'lucide-react'
import type { ReactNode } from 'react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'

import { UpgradePlanFeatureList, UpgradePlanHighlights } from './upgrade-plan-benefits.atom'

export interface UpgradePlanContentProps {
  onUpgrade: () => void
  isLoading?: boolean
  monthlyPrice?: number
  yearlyPrice?: number
  className?: string
}

const formatPrice = (price: number): string => `$${price}`

export const UpgradePlanContent: React.FC<UpgradePlanContentProps> = ({
  onUpgrade,
  isLoading = false,
  monthlyPrice,
  yearlyPrice,
  className,
}): ReactNode => (
  <ScrollArea className={cn('upgrade-plan-content h-full', className)}>
    <div className="flex flex-col gap-6 p-6">
      <UpgradePlanHeader />

      <UpgradePlanHighlights />

      <Separator />

      <UpgradePlanPricing monthlyPrice={monthlyPrice} yearlyPrice={yearlyPrice} />

      <Button
        onClick={onUpgrade}
        disabled={isLoading}
        size="lg"
        className="upgrade-plan-button w-full"
      >
        <RocketIcon className="mr-2 size-4" />
        {isLoading ? 'Processing...' : 'Upgrade to Pro'}
      </Button>

      <Separator />

      <UpgradePlanAllFeaturesSection />
    </div>
  </ScrollArea>
)

const UpgradePlanHeader: React.FC = (): ReactNode => (
  <div className="flex flex-col gap-3 text-center">
    <div className="mx-auto flex size-16 items-center justify-center rounded-full bg-gradient-to-br from-primary/20 to-primary/5">
      <SparklesIcon className="size-8 text-primary" />
    </div>
    <div className="flex flex-col gap-1">
      <h2 className="text-xl font-semibold">Upgrade to Pro</h2>
      <p className="text-sm text-muted-foreground">
        Unleash your creativity. The sky&apos;s the limit.
      </p>
    </div>
    <Badge variant="secondary" className="mx-auto">
      Most Popular
    </Badge>
  </div>
)

interface UpgradePlanPricingProps {
  monthlyPrice?: number
  yearlyPrice?: number
}

const UpgradePlanPricing: React.FC<UpgradePlanPricingProps> = ({
  monthlyPrice,
  yearlyPrice,
}): ReactNode => {
  if (!monthlyPrice && !yearlyPrice) {
    return null
  }

  return (
    <div className="flex flex-col gap-2 rounded-lg bg-muted/50 p-4 text-center">
      {monthlyPrice && (
        <div className="flex items-baseline justify-center gap-1">
          <span className="text-3xl font-bold">{formatPrice(monthlyPrice)}</span>
          <span className="text-muted-foreground">/month</span>
        </div>
      )}
      {yearlyPrice && monthlyPrice && (
        <p className="text-xs text-muted-foreground">
          or {formatPrice(yearlyPrice)}/year (save{' '}
          {Math.round(((monthlyPrice * 12 - yearlyPrice) / (monthlyPrice * 12)) * 100)}%)
        </p>
      )}
    </div>
  )
}

const UpgradePlanAllFeaturesSection: React.FC = (): ReactNode => (
  <div className="flex flex-col gap-4">
    <h3 className="text-sm font-medium text-muted-foreground">Everything in Pro</h3>
    <UpgradePlanFeatureList />
  </div>
)
